/**
 * Client Sync — Fetches all data from server API into local IndexedDB on boot.
 *
 * This runs ONLY on multi-user client (port 7743).
 * After sync, all existing Dexie-based code works unchanged because
 * data is now in the local IndexedDB.
 */

import { db } from '../db/db';

export async function syncFromServer(): Promise<{ success: boolean; message: string }> {
  const serverURL = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;

  try {
    // 1. Ping check
    const ping = await fetch(`${serverURL}/api/ping`, { signal: AbortSignal.timeout(5000) });
    const pingData = await ping.json();
    if (!pingData.pong) throw new Error('Server not responding');

    // 2. Fetch all data from server
    const [companies, groups, ledgers, vouchers, voucherEntries, inventoryEntries,
      stockItems, stockGroups, stockCategories, units, locations, voucherTypes,
      currencies, quotations] = await Promise.all([
      fetchJSON(`${serverURL}/api/companies`),
      fetchJSON(`${serverURL}/api/groups`),
      fetchJSON(`${serverURL}/api/ledgers`),
      fetchJSON(`${serverURL}/api/vouchers`),
      fetchJSON(`${serverURL}/api/voucherEntries`),
      fetchJSON(`${serverURL}/api/inventoryEntries`),
      fetchJSON(`${serverURL}/api/stockItems`),
      fetchJSON(`${serverURL}/api/stockGroups`),
      fetchJSON(`${serverURL}/api/stockCategories`),
      fetchJSON(`${serverURL}/api/units`),
      fetchJSON(`${serverURL}/api/locations`),
      fetchJSON(`${serverURL}/api/voucherTypes`),
      fetchJSON(`${serverURL}/api/currencies`),
      fetchJSON(`${serverURL}/api/quotations`),
    ]);

    // 3. Clear local DB and populate with server data
    await db.transaction('rw',
      [db.companies, db.groups, db.ledgers, db.vouchers, db.voucherEntries,
       db.inventoryEntries, db.stockItems, db.stockGroups, db.stockCategories,
       db.units, db.locations, db.voucherTypes, db.currencies, (db as any).quotations],
      async () => {
        // Clear all tables
        await db.companies.clear();
        await db.groups.clear();
        await db.ledgers.clear();
        await db.vouchers.clear();
        await db.voucherEntries.clear();
        await db.inventoryEntries.clear();
        await db.stockItems.clear();
        await db.stockGroups.clear();
        await db.stockCategories.clear();
        await db.units.clear();
        await db.locations.clear();
        await db.voucherTypes.clear();
        await db.currencies.clear();
        try { await (db as any).quotations.clear(); } catch {}

        // Bulk insert server data
        if (companies.length) await db.companies.bulkPut(companies);
        if (groups.length) await db.groups.bulkPut(groups);
        if (ledgers.length) await db.ledgers.bulkPut(ledgers);
        if (vouchers.length) await db.vouchers.bulkPut(vouchers);
        if (voucherEntries.length) await db.voucherEntries.bulkPut(voucherEntries);
        if (inventoryEntries.length) await db.inventoryEntries.bulkPut(inventoryEntries);
        if (stockItems.length) await db.stockItems.bulkPut(stockItems);
        if (stockGroups.length) await db.stockGroups.bulkPut(stockGroups);
        if (stockCategories.length) await db.stockCategories.bulkPut(stockCategories);
        if (units.length) await db.units.bulkPut(units);
        if (locations.length) await db.locations.bulkPut(locations);
        if (voucherTypes.length) await db.voucherTypes.bulkPut(voucherTypes);
        if (currencies.length) await db.currencies.bulkPut(currencies);
        if (quotations.length) try { await (db as any).quotations.bulkPut(quotations); } catch {}
      }
    );

    const totalRecords = companies.length + groups.length + ledgers.length + vouchers.length +
      voucherEntries.length + stockItems.length + voucherTypes.length;

    return { success: true, message: `Synced ${totalRecords} records from server` };
  } catch (e: any) {
    return { success: false, message: `Sync failed: ${e.message}` };
  }
}

async function fetchJSON(url: string): Promise<any[]> {
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(10000) });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

/**
 * Push local changes back to server after any write operation.
 * Call this after creating/updating vouchers, ledgers, etc.
 */
export async function pushToServer(table: string, data: any, method: 'POST' | 'PUT' = 'POST'): Promise<boolean> {
  const serverURL = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
  try {
    const url = method === 'PUT' && data.id
      ? `${serverURL}/api/${table}/${data.id}`
      : `${serverURL}/api/${table}`;
    await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return true;
  } catch {
    return false;
  }
}
